import { Box, Button, CircularProgress } from '@mui/material';
import { GridColDef, GridEditInputCell, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import LoadingSpinner from 'components/loading-spinner';
import Switch from 'components/switch';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isValidDecimal, isValidMeanWeight } from 'utils/validate-numbers';
import { getHarvest, putHarvest } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { UpdateHarvest } from 'views/production-systems/types/harvest.type';
import {
  calculateBiomass,
  calculateHarvestQty,
  calculateMeanWeight,
  kgToLbs,
  lbsToKg,
} from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../components/registration-header';

type Props = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type ParamTypes = {
  globalSiteId: string;
};

type Row = {
  id: number;
  nextCycleId: number;
  cycleName: string;
  pondName: string;
  previousQty: number;
  previousMw: number;
  previousBm: number;
  harvestQty: number;
  harvestMw: number;
  harvestBm: number;
  isPartialHarvest: boolean;
  remainingQty: number;
  remainingMw: number;
  remainingBm: number;
  pondId: string;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.harvest.edit';

const EditHarvest: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const { lockedPonds, globalSiteId, lockPonds, releasePonds } = usePondsState();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const apiRef = useGridApiRef();
  const snackbarDispatch = useSnackbarDispatch();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [enableKG, setEnableKg] = useState(false);

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<Row>) => {
      const updatedRow = {
        ...newRow,
        harvestQty: calculateHarvestQty(newRow.harvestBm, newRow.harvestMw),
        remainingQty: calculateHarvestQty(newRow.remainingBm, newRow.remainingMw),
      };
      setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
      return updatedRow;
    },
    [rows]
  );

  //#region FETCH DATA

  const { data: harvest, isFetching } = useGet(
    ['getHarvest', globalSiteId, logId, eventNr],
    ({ queryKey }) => getHarvest(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  useEffect(() => {
    if (harvest && canEdit) lockPonds(harvest.cycles.map((cycle) => cycle.pondId));
  }, [canEdit, harvest, lockPonds]);

  //#endregion FETCH DATA

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.harvestPond` }),
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {!!row.id ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'previousQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.number` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'previousMw',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.meanWeight` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'previousBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.biomass` }, { type: enableKG ? 'kg' : 'lbs' }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
        valueGetter: (value, row) => calculateBiomass(row.previousMw, row.previousQty, enableKG),
      },
      {
        field: 'harvestQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.harvestNumber` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'harvestMw',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.harvestMw` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: !readOnly,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-harvestMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'harvestBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.harvestBm` }, { type: enableKG ? 'kg' : 'lbs' }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: !readOnly,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-harvestBm`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'remainingQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.remainingNumber` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'remainingMw',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.remainingMw` }),
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        type: 'number',
        cellClassName: ({ row }) => {
          return row.isPartialHarvest ? '' : 'cell--disabled';
        },
        editable: !readOnly,
        renderEditCell: (params) => <GridEditInputCell {...params} disabled={!params.row.isPartialHarvest} />,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'remainingBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.remainingBm` }, { type: enableKG ? 'kg' : 'lbs' }),
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        type: 'number',
        cellClassName: ({ row }) => {
          return row.isPartialHarvest ? '' : 'cell--disabled';
        },
        editable: !readOnly,
        renderEditCell: (params) => <GridEditInputCell {...params} disabled={!params.row.isPartialHarvest} />,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingBm`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
    ],
    [readOnly, enableKG]
  );

  const summaryValues: SummaryItem[] = useMemo(() => {
    let summary = rows.reduce(
      (prev, curr, idx) => {
        return {
          previousQty: Number(prev.previousQty) + Number(curr.previousQty),
          previousBm: calculateBiomass(curr.previousMw, curr.previousQty, enableKG),
          previousBmKg: Number(prev.previousBmKg) + calculateBiomass(curr.previousMw, curr.previousQty, true),
          harvestQty: Number(prev.harvestQty) + Number(curr.harvestQty),
          harvestBm: Number(prev.harvestBm) + Math.round(calculateBiomass(curr.harvestMw, curr.harvestQty, enableKG)),
          harvestMwKg: Number(prev.harvestMw) + curr.harvestMw,
          remainingQty: Number(prev.remainingQty) + Number(curr.remainingQty),
          remainingBm: Number(prev.remainingBm) + Math.round(calculateBiomass(curr.remainingMw, curr.remainingQty, enableKG)),
          remainingBmKg: Number(prev.remainingMw) + curr.remainingMw,
          previousMw: 0,
          harvestMw: 0,
          remainingMw: 0,
        };
      },
      {
        previousQty: 0,
        previousBm: 0,
        previousBmKg: 0,
        previousMw: 0,
        harvestQty: 0,
        harvestMwKg: 0,
        harvestBm: 0,
        harvestMw: 0,
        remainingBm: 0,
        remainingQty: 0,
        remainingMw: 0,
        remainingBmKg: 0,
      }
    );

    summary.harvestQty = Math.round(summary.harvestQty);
    summary.remainingQty = Math.round(summary.remainingQty);
    summary.previousMw = calculateMeanWeight(summary.previousBmKg, summary.previousQty);
    summary.harvestMw = summary.harvestMwKg;
    summary.remainingMw = summary.remainingBmKg;

    return [
      {
        value: summary.previousQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.previousMw,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.previousBm,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.harvestQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.harvestMw,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.harvestBm,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.remainingQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.remainingMw,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.remainingBm,
        type: 'number',
        hidden: false,
      },
    ];
  }, [rows, enableKG]);

  //#endregion FORMS

  //#region HANDLERS

  const handleSubmit = () => {
    rows.forEach((cycle) => {
      cycle.remainingQty = Math.round(cycle.remainingQty);
      cycle.harvestQty = Math.round(cycle.harvestQty);
    });

    let d: UpdateHarvest = {
      cycles: rows.map((row) => ({
        ...row,
      })),
      processorCode: harvest?.processorCode || '',
    };

    updateHarvest.mutate({
      globalSiteId,
      data: d,
    });
  };

  //#endregion HANDLERS

  //#region UPDATE DATA

  const updateHarvest = usePost<AxiosResponse, { globalSiteId: string; data: UpdateHarvest }>(
    (request) => putHarvest(params.globalSiteId, logId, request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      harvest && releasePonds(harvest.cycles.map((cycle) => cycle.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion UPDATE DATA

  //#region HOOKS

  useEffect(() => {
    if (canEdit) {
      if (lockedPonds.notGranted.length > 0 && confirm) {
        setReadOnly(true);
      } else {
        setReadOnly(false);
      }
    }
  }, [confirm, canEdit, lockedPonds.notGranted]);

  useEffect(() => {
    setRows((oldRows) =>
      oldRows.map((newRow) => ({
        ...newRow,
        harvestBm: enableKG ? lbsToKg(newRow.harvestBm) : kgToLbs(newRow.harvestBm),
        remainingBm: enableKG ? lbsToKg(newRow.remainingBm) : kgToLbs(newRow.remainingBm),
      }))
    );
  }, [enableKG]);

  useEffect(() => {
    if (!!harvest) {
      let rows: readonly any[] = harvest.cycles.map((cycle) => {
        return {
          id: cycle.id,
          pondId: cycle.pondId,
          pondName: cycle.pondName,
          previousQty: cycle.previousQty,
          nextCycleId: cycle.nextCycleId,
          cycleName: cycle.cycleName,
          previousMw: cycle.previousMw,
          previousBm: Math.round(calculateBiomass(cycle.previousMw, cycle.previousQty, enableKG)),
          harvestQty: cycle.harvestQty,
          harvestMw: cycle.harvestMw,
          harvestBm: Math.round(calculateBiomass(cycle.harvestMw, cycle.harvestQty, enableKG)),
          isPartialHarvest: cycle.isPartialHarvest,
          remainingQty: cycle.remainingQty,
          remainingMw: cycle.remainingMw,
          remainingBm: Math.round(calculateBiomass(cycle.remainingMw, cycle.remainingQty, enableKG)),
        };
      });

      setRows(rows);
    }
  }, [harvest, enableKG]);

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return !isValidDecimal(row.harvestMw);
    });
  }, [rows]);

  //#endregion

  return isFetching ? (
    <LoadingSpinner />
  ) : lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={8}>
      {!readOnly && <PondLockTimer handleDialogClose={handleClose} />}
      <Box width={'100%'} mb={4}>
        <RegistrationHeader dateCreated={dateCreated} />
        <DataGrid
          rows={rows}
          apiRef={apiRef}
          columns={columns}
          getRowId={(row) => row.pondId}
          processRowUpdate={processRowUpdate}
          showToolbar
          slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: `${intl.formatMessage({
                  id: 'productionSystems.registrationLog.registrationTypes.harvest',
                })}-${dayjs().format('DD/MM/YYYY')}`,
              },
              children: (
                <Switch
                  dark
                  value={enableKG}
                  onChange={() => {
                    setEnableKg((prev) => !prev);
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.switchLabel` })}
                />
              ),
            },
            footer: {
              summaryValues: summaryValues,
            } as DataGridSummaryProps,
          }}
          getCellClassName={(params) => {
            // if the harvest is read only, it is disabled.
            // if cell has a class name defined (usually to set class if partial harvest, then check that row if partial harvest is enabled. if not, then the cell is disabled)
            // else if the column is not editable, then disable
            // can this be written better? idk if this is the right solution
            if (readOnly) return 'cell--disabled';
            let cellClassName = params.colDef.cellClassName;
            if (cellClassName) return params.row.isPartialHarvest ? '' : 'cell--disabled';
            else {
              return !params.colDef.editable ? 'cell--disabled' : '';
            }
          }}
          onCellEditStop={handleEditing}
          onRowClick={({ id }) => {
            const { field } = apiRef.current?.state.tabIndex.cell ?? {};
            apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
          }}
        />
      </Box>
      {!readOnly && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            disabled={updateHarvest.isPending || disableSubmit || isEditing || hasError}
            onClick={handleSubmit}
            startIcon={updateHarvest.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            <FormattedMessage id={'general.confirm'} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EditHarvest);
